import React, { useState } from 'react';

import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';

import {
	IQuestion,
	IQuestionnaireFormWidgetProps,
} from 'src/features/questionnaire/domain/types';
import { parseDateValue } from 'src/utils/dateUtils';
import { getElipsisText } from 'src/utils/formatter';

export const getWidgetLabel = (question: IQuestion, compactMode: boolean) => {
	const labelText = question.is_required
		? `${question.text} *`
		: question.text;
	const maxLength = 15;
	let displayedLabel: string;

	if (compactMode) {
		displayedLabel = getElipsisText(labelText, maxLength);
	} else {
		displayedLabel = labelText;
	}

	return <span>{displayedLabel}</span>;
};

const DateTimePickerWidget: React.FC<IQuestionnaireFormWidgetProps> = ({
	question,
	onValueChange,
	isDisabled,
	error,
	helperText = 'This field is invalid',
	compactMode = false,
}) => {
	const rawValue = question.submitted_data?.value_datetime ?? null;

	const value = parseDateValue(rawValue);

	const [dateTimeValue, setDateTimeValue] = useState(value);

	const handleValueChange = (newValue: Date | null) => {
		setDateTimeValue(newValue);
		onValueChange(
			question,
			newValue ? newValue.getTime() : null,
			'value_datetime',
		);
	};

	const getTitle = () => {
		if (!value) {
			return `Q: ${question.text}`;
		}

		const datePart = value.toLocaleDateString();
		const timePart = value.toLocaleTimeString(undefined, {
			hour: '2-digit',
			minute: '2-digit',
		});

		return `Q: ${question.text}\n\nAns: ${datePart} ${timePart}`;
	};

	return (
		<DateTimePicker
			label={getWidgetLabel(question, compactMode)}
			value={dateTimeValue}
			disabled={isDisabled}
			sx={{ width: '100%' }}
			onChange={handleValueChange}
			slotProps={{
				textField: {
					error,
					helperText: error ? helperText : '',
					inputProps: { title: getTitle() },
				},
			}}
		/>
	);
};

export default DateTimePickerWidget;
